// src/pages/league-dashboard/live-draft/components/TradeHistoryPanel.js
// Collapsible panel showing trade history and pending trades
import React, { useState, useMemo } from 'react';
import { ArrowLeftRight, ChevronDown, ChevronUp, Clock, Check, X, Loader } from 'lucide-react';
import { dynastyTheme } from '../../../../services/colorService';

export const TradeHistoryPanel = React.memo(({
  pendingTrades = [],
  tradeHistory = [],
  myTeam,
  allPicks = [],
  allTeams = [],
  onCancelTrade,
  onOpenProposal
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Get team name by ID
  const getTeamName = (teamId) => {
    const team = allTeams.find(t => t.team_id === teamId);
    return team?.team_name || 'Unknown Team';
  };

  // Get pick display string
  const getPickDisplay = (pickId) => {
    const pick = allPicks.find(p => p.pick_id === pickId);
    if (!pick) return `Pick ${pickId}`;
    return `#${pick.overall_pick_number}`;
  };

  // Get player display string
  const getPlayerDisplay = (player) => {
    if (typeof player === 'string') return player;
    return player.full_name || 'Unknown Player';
  };

  // Format trade for display
  const formatTrade = (trade) => {
    const pickParts = trade.offering_picks?.map(getPickDisplay) || [];
    const playerParts = trade.offering_players?.map(getPlayerDisplay) || [];
    const offering = [...pickParts, ...playerParts].join(', ') || 'Nothing';

    const reqPickParts = trade.requesting_picks?.map(getPickDisplay) || [];
    const reqPlayerParts = trade.requesting_players?.map(getPlayerDisplay) || [];
    const requesting = [...reqPickParts, ...reqPlayerParts].join(', ') || 'Nothing';

    return { offering, requesting };
  };

  // Separate my proposals vs proposals to me
  const myProposals = useMemo(() =>
    pendingTrades.filter(t => t.proposing_team_id === myTeam?.team_id),
    [pendingTrades, myTeam]
  );

  const proposalsToMe = useMemo(() =>
    pendingTrades.filter(t => t.receiving_team_id === myTeam?.team_id),
    [pendingTrades, myTeam]
  );

  const totalPending = pendingTrades.length;
  const totalHistory = tradeHistory.length;

  return (
    <div className={`${dynastyTheme.components.card.base}`}>
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <ArrowLeftRight className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
          <span className={`font-medium ${dynastyTheme.classes.text.white}`}>
            Trades
          </span>
          {totalPending > 0 && (
            <span className="px-2 py-0.5 text-xs font-bold bg-yellow-500 text-black rounded-full">
              {totalPending}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Expandable content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Propose Trade button */}
          <button
            onClick={onOpenProposal}
            className={`w-full py-2 rounded font-medium transition-colors
              bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/50`}
          >
            + Propose Trade
          </button>

          {/* Proposals to me */}
          {proposalsToMe.length > 0 && (
            <div>
              <h4 className={`text-xs font-bold mb-2 ${dynastyTheme.classes.text.neutralLight} uppercase tracking-wider`}>
                Incoming ({proposalsToMe.length})
              </h4>
              <div className="space-y-2">
                {proposalsToMe.map(trade => {
                  const { offering, requesting } = formatTrade(trade);
                  return (
                    <div
                      key={trade.trade_id}
                      className="p-3 bg-yellow-500/10 rounded border border-yellow-500/30"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>
                          From: {getTeamName(trade.proposing_team_id)}
                        </span>
                        <Clock className="w-4 h-4 text-yellow-500" />
                      </div>
                      <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                        Get: {offering} | Give: {requesting}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* My proposals */}
          {myProposals.length > 0 && (
            <div>
              <h4 className={`text-xs font-bold mb-2 ${dynastyTheme.classes.text.neutralLight} uppercase tracking-wider`}>
                Sent ({myProposals.length})
              </h4>
              <div className="space-y-2">
                {myProposals.map(trade => {
                  const { offering, requesting } = formatTrade(trade);
                  return (
                    <div
                      key={trade.trade_id}
                      className="p-3 bg-gray-700/50 rounded"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>
                          To: {getTeamName(trade.receiving_team_id)}
                        </span>
                        <button
                          onClick={() => onCancelTrade?.(trade.trade_id)}
                          className="p-1 hover:bg-red-500/20 rounded transition-colors"
                          title="Cancel trade"
                        >
                          <X className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                      <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                        Give: {offering} | Get: {requesting}
                      </div>
                      <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                        <Loader className="w-3 h-3 animate-spin" />
                        Waiting for response...
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Trade history */}
          {tradeHistory.length > 0 && (
            <div>
              <h4 className={`text-xs font-bold mb-2 ${dynastyTheme.classes.text.neutralLight} uppercase tracking-wider`}>
                Completed ({tradeHistory.length})
              </h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {tradeHistory.slice(0, 5).map((trade, index) => (
                  <div
                    key={trade.trade_id || index}
                    className="p-2 bg-gray-800/50 rounded text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <Check className="w-3 h-3 text-green-400" />
                      <span className={dynastyTheme.classes.text.neutralLight}>
                        Trade completed
                        {trade.affected_picks?.length > 0 && (
                          <span className="ml-1">
                            ({trade.affected_picks.length} picks)
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {totalPending === 0 && totalHistory === 0 && (
            <div className="text-center py-4">
              <ArrowLeftRight className={`w-8 h-8 mx-auto mb-2 ${dynastyTheme.classes.text.neutralDark}`} />
              <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                No trades yet
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

TradeHistoryPanel.displayName = 'TradeHistoryPanel';

export default TradeHistoryPanel;
